import React, { useState, useEffect, useMemo } from 'react';
import { Activity, ArrowUpDown, Plus, X, ChevronDown, Target } from 'lucide-react';
import { getDrills, getGPS, getGpsCustomMetrics, deleteGpsCustomMetric, getPlayers } from '../../api';
import CustomMetricModal from './CustomMetricModal';
import { METRIC_CATALOG, evalCustomMetric } from './metricsCatalog';

const DEFAULT_VISIBLE = ['td', 'rd', 'hid', 'sd', 'sa', 'expl', 'hmld', 'maxSpeed', 'minutesPlayed'];

// Same "H:MM:SS" / "MM:SS" parsing GPSUpload.js's buildMetricsFromRows uses for total_time, kept
// as its own small copy here rather than importing from GPSUpload.js (a CSV-import page with a
// much heavier import surface this read-only table page has no other reason to pull in).
function parseTimeToMinutes(timeStr) {
  if (!timeStr) return null;
  const parts = String(timeStr).trim().split(':');
  if (parts.length === 3) {
    const [h, m, s] = parts.map(Number);
    return Math.round(h * 60 + m + s / 60);
  }
  if (parts.length === 2) {
    const [m, s] = parts.map(Number);
    return Math.round(m + s / 60);
  }
  return null;
}

const num = v => (v === null || v === undefined || v === '' ? 0 : parseFloat(v)) || 0;
// Max speed keeps "no value" (2026-11-02, match gaps Stage C): a match player with no half and no run has an empty
// max speed, shown as "—" and left out of the average row and of combined rows, never as 0.
const optNum = v => (v === null || v === undefined || v === '' || !isFinite(parseFloat(v)) ? null : parseFloat(v));

function fmt(value, digits = 0) {
  return value === null || value === undefined || value === '' || isNaN(value) ? '—' : Number(value).toFixed(digits);
}

function parseTimeToSeconds(timeStr) {
  if (!timeStr) return null;
  const parts = String(timeStr).trim().split(':').map(Number);
  if (parts.some(isNaN)) return null;
  if (parts.length === 3) { const [h, m, s] = parts; return h * 3600 + m * 60 + s; }
  if (parts.length === 2) { const [m, s] = parts; return m * 60 + s; }
  if (parts.length === 1) return parts[0];
  return null;
}
function formatSecondsToTime(totalSeconds) {
  if (totalSeconds === null || totalSeconds === undefined || isNaN(totalSeconds)) return '—';
  const s = Math.round(totalSeconds);
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${String(rem).padStart(2, '0')}`;
}

const inputStyle = { padding: '9px 12px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 13, outline: 'none' };

// Local calendar dates — toISOString() gives the UTC date (yesterday until 01:00/02:00 NL time).
function localDateStr(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; }
function todayStr() { return localDateStr(new Date()); }
function daysAgoStr(n) { const d = new Date(); d.setDate(d.getDate() - n); return localDateStr(d); }

// Drills are fetched once for this whole wide window, then every date/drill/player filter below
// is applied client-side — lets picking specific session dates work instantly with no refetch.
const WIDE_FROM = '2020-01-01';

// Collapses several per-drill rows for the same player+session into one combined row — the
// "combine selected drills" feature. Base metrics aggregate per their catalog `aggregate` hint
// (sum by default, avg/max where the catalog says so; isTime columns always sum as durations).
// Custom metrics are recomputed from the combined base values rather than aggregated themselves
// (e.g. a ratio like Distance ÷ Entries must divide combined totals, not average per-drill ratios).
function aggregateGroupRows(groupRows, columns, customMetrics) {
  const first = groupRows[0];
  const combined = {
    id: `combined:${first.sessionId}:${first.playerId}`,
    sessionId: first.sessionId,
    date: first.date,
    sessionTitle: first.sessionTitle,
    drillTitle: groupRows.map(r => r.drillTitle).join(' + '),
    playerId: first.playerId,
    playerName: first.playerName,
    position: first.position,
  };
  columns.forEach(c => {
    if (c.custom) return;
    if (c.isTime) {
      const secs = groupRows.map(r => parseTimeToSeconds(r[c.key])).filter(v => v !== null);
      combined[c.key] = secs.length ? formatSecondsToTime(secs.reduce((a, b) => a + b, 0)) : '';
      return;
    }
    const nums = groupRows.map(r => r[c.key]).filter(v => v !== null && v !== undefined && isFinite(v));
    if (!nums.length) { combined[c.key] = null; return; }
    if (c.aggregate === 'avg') combined[c.key] = nums.reduce((a, b) => a + b, 0) / nums.length;
    else if (c.aggregate === 'max') combined[c.key] = Math.max(...nums);
    else combined[c.key] = nums.reduce((a, b) => a + b, 0);
  });
  // Recompute the rate from combined totals instead of using the generic 'avg' aggregation above
  // — averaging per-drill rates would misweight a short drill the same as a long one.
  if (combined.minutesPlayed) combined.distPerMin = combined.td / combined.minutesPlayed;
  customMetrics.forEach(cm => { combined[`custom:${cm.id}`] = evalCustomMetric(combined, cm); });
  return combined;
}

export default function MetricsTable({ user = null, onNavigate = null }) {
  const [dateFrom, setDateFrom] = useState(() => daysAgoStr(30));
  const [dateTo, setDateTo] = useState(() => todayStr());
  const [selectedSessionDates, setSelectedSessionDates] = useState(new Set()); // non-empty overrides From/To
  const [datesPickerOpen, setDatesPickerOpen] = useState(false);
  const [drillRows, setDrillRows] = useState([]);
  const [sessionsById, setSessionsById] = useState({});
  const [playersById, setPlayersById] = useState(null); // null = not loaded yet; else id -> { active, position }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [customMetrics, setCustomMetrics] = useState([]);
  const [visibleMetrics, setVisibleMetrics] = useState(DEFAULT_VISIBLE);
  const [selectedDrills, setSelectedDrills] = useState(new Set()); // empty = no filter (all)
  const [drillPickerOpen, setDrillPickerOpen] = useState(false);
  // New, additive on top of the normal one-row-per-player-per-drill view: with 2+ drills
  // selected, this sums/aggregates them into a single row per player per session instead of
  // showing one row per drill. Only meaningful with 2+ drills selected — see combineDrills below.
  const [combineSelectedDrills, setCombineSelectedDrills] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(''); // '' = all players
  const [selectedPosition, setSelectedPosition] = useState(''); // '' = all positions
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [modalOpen, setModalOpen] = useState(false);

  // Editing the From/To range hands filtering control back to it, clearing any specific dates
  // picked in the dropdown (which otherwise takes priority — see filteredRows below).
  const handleDateFromChange = v => { setDateFrom(v); setSelectedSessionDates(new Set()); };
  const handleDateToChange = v => { setDateTo(v); setSelectedSessionDates(new Set()); };
  const toggleSessionDate = date => setSelectedSessionDates(prev => {
    const next = new Set(prev);
    next.has(date) ? next.delete(date) : next.add(date);
    return next;
  });

  const loadCustomMetrics = () => getGpsCustomMetrics().then(setCustomMetrics).catch(err => console.error('Failed to load custom metrics:', err));

  useEffect(() => { loadCustomMetrics(); }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([getDrills({ from: WIDE_FROM, to: todayStr() }), getGPS(), getPlayers()])
      .then(([drills, sessions, players]) => {
        if (cancelled) return;
        setDrillRows(drills || []);
        const byId = {};
        (sessions || []).forEach(s => { byId[s.id] = { date: s.session_date?.slice(0, 10), title: s.session_title }; });
        setSessionsById(byId);
        const byPlayer = {};
        (players || []).forEach(p => { byPlayer[String(p.id)] = { active: p.active !== false, position: p.position || '' }; });
        setPlayersById(byPlayer);
        // Einstein deep link (?drill_contains=<text>) — read here, inside this async .then(),
        // not a useState lazy initializer or a bare mount effect. Real bug, confirmed live
        // 2026-09-10: a lazy initializer runs synchronously DURING this component's first
        // render, which can be before App.js's handlePageChange has actually finished writing
        // the new URL (setSearchParams's underlying history.replaceState isn't guaranteed to
        // have landed at that exact synchronous point) — the trainer landed on the right page
        // but the filter never applied, because drill_contains was read as null off the
        // still-stale previous URL. Every other deep-link fix in this project reads
        // window.location.search inside the .then() of an async data-fetch effect specifically
        // (PlayerChronic.js, MaxSpeed.js, EWMA.js, IndividualTargets.js all do this, not just
        // "any useEffect") — matching that exact proven pattern here instead of a bare
        // useEffect(() => {...}, []), which fires sooner and isn't proven safe the same way.
        const drillContains = new URLSearchParams(window.location.search).get('drill_contains');
        if (drillContains) setPendingDrillContains(drillContains);
      })
      .catch(err => { console.error('Failed to load Metrics Table data:', err); if (!cancelled) setError('Failed to load GPS data.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // One row per player per drill, joined with its session's date/title, and pre-computed with
  // every derived/custom value so sorting and display both read plain fields off the same object.
  // Retired players are excluded outright (playersById is null only until the initial players
  // fetch resolves, alongside drills/sessions in the same load).
  const rows = useMemo(() => {
    return drillRows
      .filter(d => !playersById || playersById[String(d.player_id)]?.active)
      .map(d => {
        const session = sessionsById[d.session_id] || {};
        const entriesZone5 = num(d.entries_zone5);
        const entriesZone6 = num(d.entries_zone6);
        const row = {
          id: d.id,
          sessionId: d.session_id,
          date: session.date || '',
          sessionTitle: session.title || '',
          drillTitle: d.drill_title || '',
          playerId: d.player_id,
          playerName: d.player_name || 'Unknown',
          position: playersById?.[String(d.player_id)]?.position || '',
          td: num(d.td), rd: num(d.rd), hid: num(d.hid), sd: num(d.sd), sa: num(d.sa),
          expl: num(d.expl), hmld: num(d.hmld), maxSpeed: optNum(d.max_speed),
          minutesPlayed: parseTimeToMinutes(d.total_time),
          distPerMin: num(d.dist_per_min),
          entriesZone5, entriesZone6, entriesZone56: entriesZone5 + entriesZone6,
          avgHr: num(d.avg_hr), maxHr: num(d.max_hr),
          avgMetabolicPower: num(d.avg_metabolic_power), hrv: num(d.hrv), modTrimp: num(d.mod_trimp),
          hrZ1: d.hr_z1 || '', hrZ2: d.hr_z2 || '', hrZ3: d.hr_z3 || '',
          hrZ4: d.hr_z4 || '', hrZ5: d.hr_z5 || '', hrZ6: d.hr_z6 || '',
        };
        customMetrics.forEach(cm => { row[`custom:${cm.id}`] = evalCustomMetric(row, cm); });
        return row;
      });
  }, [drillRows, sessionsById, customMetrics, playersById]);

  // Date filter applied first, on its own, so the drill picker below can be scoped to just the
  // drills that actually occur in the selected date(s)/range rather than every drill ever logged.
  const dateFilteredRows = useMemo(() => {
    return rows.filter(r => {
      if (selectedSessionDates.size) return selectedSessionDates.has(r.date);
      if (dateFrom && r.date < dateFrom) return false;
      if (dateTo && r.date > dateTo) return false;
      return true;
    });
  }, [rows, selectedSessionDates, dateFrom, dateTo]);

  const drillTitles = useMemo(() => [...new Set(dateFilteredRows.map(r => r.drillTitle).filter(Boolean))].sort(), [dateFilteredRows]);
  const playerNames = useMemo(() => [...new Set(rows.map(r => r.playerName).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [rows]);
  const positions = useMemo(() => [...new Set(rows.map(r => r.position).filter(Boolean))].sort((a, b) => a.localeCompare(b)), [rows]);
  // Every session date that exists, independent of the current From/To range, so a specific
  // date is always pickable even if it falls outside the currently-active range filter. Label
  // includes the session title(s) on that date (e.g. "3-09-2026 MD-1") so same-day sessions are
  // distinguishable in the dropdown — filtering itself still keys off the raw date, since a row
  // only carries its own session's date, not which title was clicked.
  const sessionDateOptions = useMemo(() => {
    const titlesByDate = {};
    Object.values(sessionsById).forEach(s => {
      if (!s.date) return;
      if (!titlesByDate[s.date]) titlesByDate[s.date] = new Set();
      if (s.title) titlesByDate[s.date].add(s.title);
    });
    return Object.keys(titlesByDate).sort().reverse().map(date => {
      const [y, m, d] = date.split('-');
      const titles = [...titlesByDate[date]].join(', ');
      return { date, label: `${Number(d)}-${m}-${y}${titles ? ' ' + titles : ''}` };
    });
  }, [sessionsById]);

  // Einstein deep link (2026-09-10, real bug report) — "what's the physical load in the last
  // SSG?" routes here with ?drill_contains=<text> (read above, inside the data-fetch effect's
  // .then() — see that block's comment for why it moved there from a useState lazy initializer,
  // which was the actual bug: it ran too early, before the URL had really been updated). SSG (the
  // DRILL type — physical load data) was getting confused with SSG the Period League COMPETITION
  // (registry key ssg_period_league) — a completely different page with no load metrics at all,
  // just standings/results. Resolved once `rows` (the full WIDE_FROM history) is loaded: finds
  // every row whose drillTitle contains the text, picks the MOST RECENT date among them, and
  // pre-filters the table to that one date plus those specific drill title(s) — "the latest drill
  // containing X", not just any-time-ever. Consumed once (cleared after applying, whether or not
  // a match was found) so it doesn't keep overriding a filter the trainer manually changes after.
  const [pendingDrillContains, setPendingDrillContains] = useState(null);
  useEffect(() => {
    if (!pendingDrillContains || loading || !rows.length) return;
    const needle = pendingDrillContains.toLowerCase();
    const matches = rows.filter(r => r.drillTitle.toLowerCase().includes(needle));
    if (matches.length) {
      const latestDate = matches.reduce((max, r) => (r.date > max ? r.date : max), matches[0].date);
      setSelectedSessionDates(new Set([latestDate]));
      setSelectedDrills(new Set(matches.filter(r => r.date === latestDate).map(r => r.drillTitle)));
    }
    setPendingDrillContains(null);
  }, [pendingDrillContains, loading, rows]);

  // The selected player can fall out of the list when the date range changes (e.g. they didn't
  // train in the new window) — reset rather than leave a phantom selection with no matching option.
  useEffect(() => {
    if (selectedPlayer && !playerNames.includes(selectedPlayer)) setSelectedPlayer('');
  }, [playerNames, selectedPlayer]);

  useEffect(() => {
    if (selectedPosition && !positions.includes(selectedPosition)) setSelectedPosition('');
  }, [positions, selectedPosition]);

  // Same idea for drills: a selected drill that no longer occurs in the active date filter is
  // dropped from the selection instead of silently filtering everything to zero rows.
  useEffect(() => {
    if (!selectedDrills.size) return;
    const stillValid = [...selectedDrills].filter(t => drillTitles.includes(t));
    if (stillValid.length !== selectedDrills.size) setSelectedDrills(new Set(stillValid));
  }, [drillTitles, selectedDrills]);

  const columns = useMemo(() => [
    ...METRIC_CATALOG,
    ...customMetrics.map(cm => ({ key: `custom:${cm.id}`, label: cm.name, unit: cm.unit || '', digits: 2, custom: cm })),
  ], [customMetrics]);

  const filteredRows = useMemo(() => {
    return dateFilteredRows.filter(r => {
      if (selectedDrills.size && !selectedDrills.has(r.drillTitle)) return false;
      if (selectedPlayer && r.playerName !== selectedPlayer) return false;
      if (selectedPosition && r.position !== selectedPosition) return false;
      return true;
    });
  }, [dateFilteredRows, selectedDrills, selectedPlayer, selectedPosition]);

  // Combining only makes sense with 2+ drills actually selected — auto-clear the toggle rather
  // than leave it silently checked-but-inert once the selection drops back below that.
  useEffect(() => {
    if (combineSelectedDrills && selectedDrills.size < 2) setCombineSelectedDrills(false);
  }, [combineSelectedDrills, selectedDrills]);

  const combineDrills = combineSelectedDrills && selectedDrills.size >= 2;

  // New feature, additive to the normal one-row-per-player-per-drill table: when combining,
  // group the (already drill/player/position-filtered) rows by session+player and collapse each
  // group into a single aggregated row, instead of replacing how individual-drill rows work.
  const displayRows = useMemo(() => {
    if (!combineDrills) return filteredRows;
    const groups = new Map();
    filteredRows.forEach(r => {
      const key = `${r.sessionId}:${r.playerId}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    });
    return [...groups.values()].map(groupRows => aggregateGroupRows(groupRows, columns, customMetrics));
  }, [filteredRows, combineDrills, columns, customMetrics]);

  const sortedRows = useMemo(() => {
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...displayRows].sort((a, b) => {
      const va = a[sortKey]; const vb = b[sortKey];
      if (typeof va === 'string' || typeof vb === 'string') return String(va ?? '').localeCompare(String(vb ?? '')) * dir;
      return ((va ?? -Infinity) - (vb ?? -Infinity)) * dir;
    });
  }, [displayRows, sortKey, sortDir]);

  const shownColumns = columns.filter(c => visibleMetrics.includes(c.key));
  const allSelected = visibleMetrics.length === columns.length;

  // Average across every currently-filtered/sorted row, per visible column — isTime columns
  // (hh:mm:ss strings) are averaged in seconds and reformatted rather than skipped.
  const averageRow = useMemo(() => {
    const avg = {};
    shownColumns.forEach(c => {
      const values = sortedRows.map(r => r[c.key]).filter(v => v !== null && v !== undefined && v !== '');
      if (c.isTime) {
        const secs = values.map(parseTimeToSeconds).filter(v => v !== null);
        avg[c.key] = secs.length ? formatSecondsToTime(secs.reduce((a, b) => a + b, 0) / secs.length) : '—';
      } else {
        const nums = values.map(Number).filter(v => isFinite(v));
        avg[c.key] = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : null;
      }
    });
    return avg;
  }, [sortedRows, shownColumns]);

  const toggleMetric = key => setVisibleMetrics(v => v.includes(key) ? v.filter(k => k !== key) : [...v, key]);
  const toggleAllMetrics = () => setVisibleMetrics(allSelected ? [] : columns.map(c => c.key));

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // Players.js reads ?player_id= on mount and opens that player's profile modal (same
  // deep-link param convention EWMA/PlayerChronic/MaxSpeed/MatchChronic already use).
  const goToPlayerProfile = playerId => {
    if (!onNavigate || !playerId) return;
    onNavigate('players', null, new URLSearchParams({ player_id: String(playerId) }));
  };

  const toggleDrill = title => setSelectedDrills(prev => {
    const next = new Set(prev);
    next.has(title) ? next.delete(title) : next.add(title);
    return next;
  });

  const handleDeleteCustomMetric = async (id) => {
    if (!window.confirm('Delete this calculated metric? Every staff member will lose access to this column.')) return;
    try {
      await deleteGpsCustomMetric(id);
      setVisibleMetrics(v => v.filter(k => k !== `custom:${id}`));
      loadCustomMetrics();
    } catch (err) {
      console.error('Failed to delete custom metric:', err);
      alert('Failed to delete metric.');
    }
  };

  const thStyle = { padding: '10px 12px', textAlign: 'right', fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none' };

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Metrics Table</h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 14, marginTop: 4 }}>Every player/drill GPS metric, filterable by date and drill.</p>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center', marginBottom: 16 }}>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setDatesPickerOpen(o => !o)}
            style={{ ...inputStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            Session dates {selectedSessionDates.size ? `(${selectedSessionDates.size})` : '(range below)'} <ChevronDown size={14} />
          </button>
          {datesPickerOpen && (
            <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: 10, minWidth: 200, maxHeight: 320, overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
              <button onClick={() => setSelectedSessionDates(new Set())} style={{ fontSize: 12, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', padding: '2px 0', marginBottom: 6 }}>Clear (use range below)</button>
              {sessionDateOptions.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>No sessions found.</div>}
              {sessionDateOptions.map(opt => (
                <label key={opt.date} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedSessionDates.has(opt.date)} onChange={() => toggleSessionDate(opt.date)} style={{ accentColor: '#3b82f6' }} />
                  {opt.label}
                </label>
              ))}
            </div>
          )}
        </div>
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>From</label>
        <input type="date" value={dateFrom} onChange={e => handleDateFromChange(e.target.value)} style={inputStyle} />
        <label style={{ fontSize: 12, color: 'var(--text-muted)' }}>To</label>
        <input type="date" value={dateTo} onChange={e => handleDateToChange(e.target.value)} style={inputStyle} />
        <div style={{ position: 'relative' }}>
          <button onClick={() => setDrillPickerOpen(o => !o)}
            style={{ ...inputStyle, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
            Drills {selectedDrills.size ? `(${selectedDrills.size}${combineDrills ? ', combined' : ''})` : '(all)'} <ChevronDown size={14} />
          </button>
          {drillPickerOpen && (
            <div style={{ position: 'absolute', top: '110%', left: 0, zIndex: 20, background: 'var(--bg-app)', border: '1px solid var(--border-strong)', borderRadius: 8, padding: 10, minWidth: 240, maxHeight: 300, overflowY: 'auto', boxShadow: '0 10px 30px rgba(0,0,0,0.25)' }}>
              <button onClick={() => setSelectedDrills(new Set())} style={{ fontSize: 12, background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', padding: '2px 0', marginBottom: 6 }}>Clear (show all)</button>
              {drillTitles.length === 0 && <div style={{ fontSize: 12, color: 'var(--text-dimmed)' }}>No drills in range.</div>}
              {drillTitles.map(title => (
                <label key={title} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '4px 0', cursor: 'pointer' }}>
                  <input type="checkbox" checked={selectedDrills.has(title)} onChange={() => toggleDrill(title)} style={{ accentColor: '#3b82f6' }} />
                  {title}
                </label>
              ))}
              {selectedDrills.size >= 2 && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, padding: '8px 0 2px', marginTop: 6, borderTop: '1px solid var(--border-strong)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={combineSelectedDrills} onChange={e => setCombineSelectedDrills(e.target.checked)} style={{ accentColor: '#3b82f6' }} />
                  Combine into one row per player
                </label>
              )}
            </div>
          )}
        </div>
        <select value={selectedPlayer} onChange={e => setSelectedPlayer(e.target.value)} style={{ ...inputStyle, minWidth: 200, cursor: 'pointer' }}>
          <option value="">All players</option>
          {playerNames.map(name => (<option key={name} value={name}>{name}</option>))}
        </select>
        <select value={selectedPosition} onChange={e => setSelectedPosition(e.target.value)} style={{ ...inputStyle, minWidth: 140, cursor: 'pointer' }}>
          <option value="">All positions</option>
          {positions.map(pos => (<option key={pos} value={pos}>{pos}</option>))}
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {onNavigate && (user?.isAdmin || (user?.allowedTabs || []).includes('drill-performance')) && (
            <button onClick={() => onNavigate('drill-performance')}
              style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Target size={14} /> Drill vs match targets
            </button>
          )}
          <button onClick={() => setModalOpen(true)}
            style={{ padding: '9px 14px', borderRadius: 8, border: 'none', background: '#3b82f6', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            <Plus size={14} /> New calculated metric
          </button>
        </div>
      </div>

      {/* Column picker */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 8 }}>Show columns</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {columns.map(c => {
            const active = visibleMetrics.includes(c.key);
            return (
              <span key={c.key} style={{ display: 'inline-flex', alignItems: 'center' }}>
                <button onClick={() => toggleMetric(c.key)}
                  style={{ padding: '8px 14px', borderRadius: c.custom ? '10px 0 0 10px' : 10, border: `1px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`, borderRight: c.custom ? 'none' : undefined, background: active ? '#3b82f6' : 'var(--bg-app)', color: active ? '#fff' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  {c.label}
                </button>
                {c.custom && (
                  <button onClick={() => handleDeleteCustomMetric(c.custom.id)} title="Delete this calculated metric"
                    style={{ padding: '8px 10px', borderRadius: '0 10px 10px 0', border: `1px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`, background: active ? '#3b82f6' : 'var(--bg-app)', color: active ? '#fff' : 'var(--text-secondary)', fontSize: 12, cursor: 'pointer' }}>
                    <X size={12} />
                  </button>
                )}
              </span>
            );
          })}
        </div>
        <button onClick={toggleAllMetrics}
          style={{ marginTop: 8, padding: '8px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
          {allSelected ? 'Deselect all' : 'Select all'}
        </button>
      </div>

      {error && <div style={{ padding: 14, borderRadius: 8, background: '#451a03', color: '#fca5a5', fontSize: 13, marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40vh', color: 'var(--text-muted)', gap: 12 }}>
          <Activity size={22} strokeWidth={1.5} /><span>Loading…</span>
        </div>
      ) : (
        <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => toggleSort('date')}>Date {sortKey === 'date' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => toggleSort('sessionTitle')}>Session {sortKey === 'sessionTitle' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => toggleSort('drillTitle')}>Drill {sortKey === 'drillTitle' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                  <th style={{ ...thStyle, textAlign: 'left' }} onClick={() => toggleSort('playerName')}>Player {sortKey === 'playerName' ? (sortDir === 'asc' ? '▲' : '▼') : ''}</th>
                  {shownColumns.map(c => (
                    <th key={c.key} style={thStyle} onClick={() => toggleSort(c.key)}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{c.label}{c.unit ? ` (${c.unit})` : ''} <ArrowUpDown size={10} /></span>
                      {sortKey === c.key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r, i) => (
                  <tr key={r.id} style={{ background: i % 2 !== 0 ? 'var(--bg-row-alt)' : 'transparent', borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '8px 12px', fontSize: 12, whiteSpace: 'nowrap' }}>{r.date}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>{r.sessionTitle}</td>
                    <td style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>{r.drillTitle}</td>
                    <td style={{ padding: '8px 12px', fontSize: 13, fontWeight: 600 }}>
                      {onNavigate ? (
                        <button onClick={() => goToPlayerProfile(r.playerId)} title="Open player profile"
                          style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 600, color: '#3b82f6', cursor: 'pointer', textAlign: 'left' }}>
                          {r.playerName}
                        </button>
                      ) : r.playerName}
                    </td>
                    {shownColumns.map(c => (
                      <td key={c.key} style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' }}>
                        {c.isTime ? (r[c.key] || '—') : fmt(r[c.key], c.digits ?? 0)}
                      </td>
                    ))}
                  </tr>
                ))}
                {sortedRows.length === 0 && (
                  <tr><td colSpan={4 + shownColumns.length} style={{ padding: 30, textAlign: 'center', color: 'var(--text-dimmed)', fontSize: 13 }}>No rows for this date range/filter.</td></tr>
                )}
              </tbody>
              {sortedRows.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid var(--border-strong)', background: 'var(--border)' }}>
                    <td colSpan={4} style={{ padding: '8px 12px', fontSize: 12, fontWeight: 700 }}>Average</td>
                    {shownColumns.map(c => (
                      <td key={c.key} style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {c.isTime ? averageRow[c.key] : fmt(averageRow[c.key], c.digits ?? 0)}
                      </td>
                    ))}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}

      {modalOpen && (
        <CustomMetricModal
          user={user}
          onClose={() => setModalOpen(false)}
          onCreated={(created) => {
            setModalOpen(false);
            loadCustomMetrics();
            setVisibleMetrics(v => [...v, `custom:${created.id}`]);
          }}
        />
      )}
    </div>
  );
}
